import { type ExtractRouteParams } from 'react-router'
import { generatePath } from 'react-router-dom'

import type { Pathname } from './types'

/**
 * Builds a route path from a pathname template and its params.
 *
 * Safe against missing params: if a required param resolves to undefined/null,
 * `generatePath` throws — this function catches that and returns `'#'` so the
 * link stays inert instead of crashing the whole subtree during render.
 *
 * As an extra guard, a missing `panelId` is replaced with `'defaultPanelId'`
 * to avoid `undefined` showing up in URLs.
 */
export function getRoute<T extends Pathname>(pathname: T, params?: ExtractRouteParams<T>) {
  const sanitizedParams = { ...params }
  if ('panelId' in sanitizedParams && !sanitizedParams.panelId) {
    sanitizedParams.panelId = 'defaultPanelId'
  }
  try {
    return generatePath(pathname, sanitizedParams)
  } catch {
    return '#'
  }
}

export function appendSearchToPathname(pathname: string, searchParams: URLSearchParams) {
  const paramsAsString = searchParams.toString()
  if (!paramsAsString.length) {
    return pathname
  }
  return `${pathname}?${paramsAsString}`
}
